import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { isAssetShareToken } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { useGetAssetShareAccessQuery, useGetPublicAssetShareQuery } from '../../../redux/api/fymAPI';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { AssetShareMissing } from './AssetShareMissing';
import { AssetSharePublicView } from './AssetSharePublicView';
import { useOpenSharedAsset } from './useOpenSharedAsset';

/** The HTTP status of an RTK Query error, or undefined. */
function statusOf(error: unknown): unknown {
  return typeof error === 'object' && error !== null && 'status' in error ? error.status : undefined;
}

const Loading: React.FC = () => (
  <SMLContainer publicMode header={null}>
    <LoadingView />
  </SMLContainer>
);

/**
 * The share link of one made asset: `/app/a/<token>` (owner decision 2026-10-07: the page lives in
 * the web app, like the Public Intelligence share links). A visitor who is not signed in, and a
 * signed-in user with no access to the workspace of the asset, see the public preview. A signed-in
 * member of that workspace never sees it (owner fix 2026-10-19): the page waits for the access
 * answer, then replaces itself with the asset in its release (`useOpenSharedAsset`), after a
 * workspace switch when needed. A failed switch shows the preview with "Open in release" as a
 * retry. A link that does not work shows the not-found page.
 */
const AssetShareViewComponent: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const validToken = isAssetShareToken(token);
  const userToken = useUserToken();
  const isAuthenticated = userToken !== null && userToken !== undefined;

  const access = useGetAssetShareAccessQuery({ token }, { skip: !validToken || !isAuthenticated });
  const share = useGetPublicAssetShareQuery({ token }, { skip: !validToken });
  // A failed access read shows the preview with no button.
  const memberAccess = isAuthenticated ? (access.data?.access ?? null) : null;
  const openShared = useOpenSharedAsset(memberAccess);
  const { open } = openShared;
  // The redirect runs once for the page view. A retry after a failed switch is the button.
  const redirected = useRef(false);
  useEffect(() => {
    if (memberAccess === null || redirected.current) {
      return;
    }
    redirected.current = true;
    open();
  }, [memberAccess, open]);

  const accessPending = isAuthenticated && validToken && access.data === undefined && access.error === undefined;
  const redirecting = memberAccess !== null && openShared.state !== 'failed';

  if (!validToken) {
    return <AssetShareMissing kind="notFound" />;
  }
  // A member must not see the public page for a moment before the redirect.
  if (accessPending || redirecting) {
    return <Loading />;
  }
  if (share.isLoading) {
    return <Loading />;
  }
  if (share.error !== undefined) {
    const status = statusOf(share.error);
    if (status === 404 || status === 410) {
      return <AssetShareMissing kind={status === 410 ? 'gone' : 'notFound'} />;
    }
    return (
      <SMLContainer publicMode header={null}>
        <ErrorView
          error={share.error}
          retry={() => {
            share.refetch().catch(console.error);
          }}
        />
      </SMLContainer>
    );
  }
  if (share.data === undefined) {
    return <Loading />;
  }
  return (
    <AssetSharePublicView
      share={share.data}
      openInRelease={memberAccess === null ? null : { onOpen: openShared.open, state: openShared.state }}
    />
  );
};

export const AssetShareView = TypedMemo(AssetShareViewComponent);
